/**
 * OnboardingProgressWidget
 *
 * Displays 6-step onboarding checklist for guides with completion tracking.
 * Shows completion percentage, next step CTA, and dismiss functionality.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: SCRUM-530 - Onboarding Progress (5 SP)
 * Story: SCRUM-548 - Added Payments Step
 * Location: src/components/guides/OnboardingProgressWidget.jsx
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle2, Circle, X, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import apiClient from '../../utils/apiClient';

export function OnboardingProgressWidget({ guideId }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    fetchProgress();
  }, [guideId]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/dashboard/onboarding-progress');

      if (response.data.success) {
        setProgress(response.data.data || null);
      }
    } catch (err) {
      console.error('Error fetching onboarding progress:', err);
      setError(err.message || 'Failed to load onboarding progress');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      setDismissing(true);

      const response = await apiClient.post('/dashboard/onboarding-dismiss');

      if (response.data.success) {
        // Update local state
        setProgress({ ...progress, isDismissed: true });
      }
    } catch (err) {
      console.error('Error dismissing onboarding:', err);
      setError('Failed to dismiss onboarding checklist');
    } finally {
      setDismissing(false);
    }
  };

  // Don't render if dismissed or fully complete
  if (progress && (progress.isDismissed || progress.isComplete)) {
    return null;
  }

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'var(--color-info-light)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '2px solid var(--color-info)'
      }}>
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error || !progress) {
    return null; // Silently fail for non-critical widget
  }

  return (
    <div style={{
      backgroundColor: 'var(--color-info-light)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      boxShadow: 'var(--shadow-md)',
      border: '2px solid var(--color-info)',
      position: 'relative'
    }}>
      {/* Dismiss Button */}
      <button
        onClick={handleDismiss}
        disabled={dismissing}
        style={{
          position: 'absolute',
          top: 'var(--space-3)',
          right: 'var(--space-3)',
          padding: 'var(--space-1)',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: dismissing ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: dismissing ? 0.5 : 0.6,
          transition: 'all var(--transition-fast)'
        }}
        onMouseOver={(  _e) => {
          if (!dismissing) {_e.currentTarget.style.opacity = '1';}
        }}
        onMouseOut={(  _e) => {
          if (!dismissing) {_e.currentTarget.style.opacity = '0.6';}
        }}
        title="Dismiss onboarding checklist"
      >
        <X size={18} style={{ color: 'var(--color-info-dark)' }} />
      </button>

      {/* Header */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-info-dark)',
          marginBottom: 'var(--space-2)',
          margin: 0
        }}>
          Complete Your Profile Setup
        </h3>
        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-info-dark)',
          margin: 0,
          opacity: 0.9
        }}>
          {progress.completedCount} of {progress.totalSteps} steps completed
        </p>
      </div>

      {/* Progress Bar */}
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-full)',
        height: '8px',
        overflow: 'hidden',
        marginBottom: 'var(--space-4)'
      }}>
        <div style={{
          backgroundColor: 'var(--color-info)',
          height: '100%',
          width: `${progress.completionPercent}%`,
          borderRadius: 'var(--radius-full)',
          transition: 'width var(--transition-slow)'
        }} />
      </div>

      {/* Steps List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        {progress.steps.map((step) => (
          <div
            key={step.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: step.completed ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
              transition: 'all var(--transition-fast)'
            }}
          >
            {/* Checkbox Icon */}
            {step.completed ? (
              <CheckCircle2 size={20} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
            ) : (
              <Circle size={20} style={{ color: 'var(--color-info-dark)', opacity: 0.6, flexShrink: 0 }} />
            )}

            {/* Step Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-info-dark)',
                textDecoration: step.completed ? 'line-through' : 'none',
                opacity: step.completed ? 0.7 : 1
              }}>
                {step.title}
              </div>
              {!step.completed && (
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-info-dark)',
                  opacity: 0.8,
                  marginTop: '2px'
                }}>
                  {step.description}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Next Step CTA */}
      {progress.nextStep && (
        <button
          onClick={() => window.location.href = progress.nextStep.ctaLink}
          style={{
            width: '100%',
            padding: 'var(--space-3)',
            backgroundColor: 'var(--color-info)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)',
            transition: 'all var(--transition-fast)'
          }}
          onMouseOver={(  _e) => {
            _e.currentTarget.style.backgroundColor = 'var(--color-info-dark)';
            _e.currentTarget.style.transform = 'translateY(-1px)';
            _e.currentTarget.style.boxShadow = 'var(--shadow-md)';
          }}
          onMouseOut={(  _e) => {
            _e.currentTarget.style.backgroundColor = 'var(--color-info)';
            _e.currentTarget.style.transform = 'translateY(0)';
            _e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {progress.nextStep.ctaText} <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

export default OnboardingProgressWidget;
